import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Eye, Download, X, ExternalLink } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const LISTS = [
    {
        key: "multicore",
        title: "KEI Wire — Multicore Price List",
        meta: "KEI LDC-MC FLX · Effective 1st Oct 2026",
        brand: "KEI",
        url: "/files/kei-multicore-price-list.pdf",
    },
    {
        key: "singlecore",
        title: "KEI Wire — Single Core & Project Price List",
        meta: "KEI LDC-SC FLX · Effective 1st Oct 2026",
        brand: "KEI",
        url: "/files/kei-single-core-price-list.pdf",
    },
    {
        key: "lt-ht",
        title: "LT Cable & HT Cable Price List",
        meta: "KEI Cable List Price · Aug 2026",
        brand: "KEI",
        url: "/files/kei-lt-ht-cable-price-list.pdf",
    },
    {
        key: "dowells",
        title: "Dowells — Glands, Lugs & Accessories Price List",
        meta: "Dowells List Price · Effective 25th Aug 2026",
        brand: "Dowells",
        url: "/files/dowells-price-list.pdf",
    },
];

export default function PriceLists() {
    const [preview, setPreview] = useState(null);

    return (
        <section id="pricelists" className="relative hud-bg py-20 lg:py-28 border-y border-line" data-testid="pricelists-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
                    <Reveal>
                        <Eyebrow className="mb-5">05 — Price Lists</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink">
                            Current price lists, <em className="text-copper">always ready.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:max-w-sm">
                        <p className="text-mist leading-relaxed">
                            Official list prices, straight from the manufacturer — KEI cables &amp; wires,
                            Dowells glands &amp; lugs. Preview any list right here, or download the PDF for
                            your purchase file.
                        </p>
                    </Reveal>
                </div>

                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {LISTS.map((l, i) => (
                        <Reveal key={l.key} delay={i * 0.08}>
                            <div
                                data-testid={`pricelist-card-${l.key}`}
                                className="range-card group rounded-md border border-line bg-white p-6 h-full transition-all duration-500 flex flex-col"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="w-11 h-11 rounded-full bg-copper-light flex items-center justify-center text-copper">
                                        <FileText size={19} />
                                    </span>
                                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-ink text-white px-2.5 py-1 rounded-full">
                                        {l.brand}
                                    </span>
                                </div>
                                <h3 className="font-display font-semibold text-xl text-ink leading-snug mb-2">
                                    {l.title}
                                </h3>
                                <p className="font-mono text-[10px] tracking-[0.15em] text-dim uppercase mb-6">
                                    {l.meta}
                                </p>
                                <div className="mt-auto flex gap-2">
                                    <button
                                        data-testid={`pricelist-preview-${l.key}`}
                                        onClick={() => setPreview(l)}
                                        className="flex-1 rounded-full border border-ink/20 text-ink font-mono uppercase tracking-[0.15em] text-[10px] py-3 hover:border-copper hover:text-copper transition-colors duration-300 flex items-center justify-center gap-2"
                                    >
                                        <Eye size={13} /> Preview
                                    </button>
                                    <a
                                        data-testid={`pricelist-download-${l.key}`}
                                        href={l.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        className="flex-1 rounded-full bg-copper text-white font-mono uppercase tracking-[0.15em] text-[10px] py-3 hover:bg-copper-bright transition-colors duration-300 flex items-center justify-center gap-2"
                                    >
                                        <Download size={13} /> Download
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {preview && (
                    <>
                        <motion.div
                            className="fixed inset-0 bg-inkdark/60 z-50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setPreview(null)}
                        />
                        <motion.div
                            data-testid="pricelist-modal"
                            className="fixed inset-x-4 top-[4vh] bottom-[4vh] sm:inset-x-10 lg:inset-x-24 bg-white rounded-md shadow-2xl z-50 flex flex-col overflow-hidden"
                            initial={{ opacity: 0, y: 40, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 40, scale: 0.97 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-line">
                                <div className="min-w-0">
                                    <p className="font-display font-semibold text-base text-ink truncate">{preview.title}</p>
                                    <p className="font-mono text-[9px] tracking-[0.15em] text-dim uppercase">{preview.meta}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <a
                                        data-testid="pricelist-modal-open-tab"
                                        href={preview.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-mist hover:text-copper hover:border-copper transition-colors duration-300"
                                        aria-label="Open in new tab"
                                    >
                                        <ExternalLink size={15} />
                                    </a>
                                    <a
                                        data-testid="pricelist-modal-download"
                                        href={preview.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-mist hover:text-copper hover:border-copper transition-colors duration-300"
                                        aria-label="Download PDF"
                                    >
                                        <Download size={15} />
                                    </a>
                                    <button
                                        data-testid="pricelist-modal-close"
                                        onClick={() => setPreview(null)}
                                        className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center hover:bg-copper transition-colors duration-300"
                                        aria-label="Close preview"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>
                            <iframe
                                src={preview.url}
                                title={preview.title}
                                className="flex-1 w-full bg-paper"
                                data-testid="pricelist-modal-frame"
                            />
                            <p className="px-5 py-2.5 border-t border-line font-mono text-[9px] tracking-[0.15em] uppercase text-dim text-center" data-testid="pricelist-modal-fallback">
                                Preview not loading on your device? Use open-in-new-tab or download above.
                            </p>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </section>
    );
}
